import { useEffect, useState } from 'react';
import {
  Divider,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  TextField,
  Grid,
  IconButton,
  Chip,
  Collapse,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Info,
  Mail,
  Calendar,
  RefreshCw,
  X,
  Bell,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Check,
  HelpCircle,
  User,
  Clock,
} from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { NotificationT, SEVERITY } from '../../types/notification';
import Notifications from '../../components/common/Notifications';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';
import { useSmsSettings } from '../../hooks/sms-settings/useSmsSettings';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const SmsSettings = () => {
  const theme = useTheme();
  const { smsSettings, isLoading, error } = useSelector((state: RootState) => state.smsSettings);
  const { selectedOrganizationId, refetch, saveSmsSettings, updateSmsSettings, deleteSmsSettings } =
    useSmsSettings();

  const { role } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [expandedHelp, setExpandedHelp] = useState(false);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      isSMSEnabled: smsSettings?.isSMSEnabled || false,
      isScheduleSMSEnabled: smsSettings?.isScheduleSMSEnabled || false,
      isRescheduleSMSEnabled: smsSettings?.isRescheduleSMSEnabled || false,
      isCancelSMSEnabled: smsSettings?.isCancelSMSEnabled || false,
      isReminderSMSEnabled: smsSettings?.isReminderSMSEnabled || false,
      scheduleSMSFormat: smsSettings?.scheduleSMSFormat || '',
      rescheduleSMSFormat: smsSettings?.rescheduleSMSFormat || '',
      cancelSMSFormat: smsSettings?.cancelSMSFormat || '',
      reminderSMSFormat: smsSettings?.reminderSMSFormat || '',
    },
  });

  // Update form values when smsSettings changes
  useEffect(() => {
    if (smsSettings) {
      setValue('isSMSEnabled', smsSettings?.isSMSEnabled || false);
      setValue('isScheduleSMSEnabled', smsSettings?.isScheduleSMSEnabled || false);
      setValue('isRescheduleSMSEnabled', smsSettings?.isRescheduleSMSEnabled || false);
      setValue('isCancelSMSEnabled', smsSettings?.isCancelSMSEnabled || false);
      setValue('isReminderSMSEnabled', smsSettings?.isReminderSMSEnabled || false);
      setValue('scheduleSMSFormat', smsSettings?.scheduleSMSFormat || '');
      setValue('rescheduleSMSFormat', smsSettings?.rescheduleSMSFormat || '');
      setValue('cancelSMSFormat', smsSettings?.cancelSMSFormat || '');
      setValue('reminderSMSFormat', smsSettings?.reminderSMSFormat || '');
    }
  }, [smsSettings, setValue]);

  // Watch the values of the toggles
  const isSMSEnabled = watch('isSMSEnabled');
  const isScheduleSMSEnabled = watch('isScheduleSMSEnabled');
  const isRescheduleSMSEnabled = watch('isRescheduleSMSEnabled');
  const isCancelSMSEnabled = watch('isCancelSMSEnabled');
  const isReminderSMSEnabled = watch('isReminderSMSEnabled');

  const [previewType, setPreviewType] = useState<string | null>(null);
  const [tryOutMessage, setTryOutMessage] = useState<string | null>(null);

  const templateVariables = [
    { name: '{customerName}', description: 'Customer name', icon: <User size={16} /> },
    {
      name: '{serviceName}',
      description: 'Service name used for the booking',
      icon: <MessageCircle size={16} />,
    },
    {
      name: '{resourceName}',
      description: 'Resource name used for the booking',
      icon: <Info size={16} />,
    },
    { name: '{startDate}', description: 'Appointment start date', icon: <Calendar size={16} /> },
    { name: '{startTime}', description: 'Appointment start time', icon: <Clock size={16} /> },
    { name: '{endDate}', description: 'Appointment end date', icon: <Calendar size={16} /> },
    { name: '{endTime}', description: 'Appointment end time', icon: <Clock size={16} /> },
  ];

  const handleTryOut = (format: string, type: string) => {
    setPreviewType(type);

    const dummyValues: Record<string, string> = {
      customerName: 'John Doe',
      serviceName: 'Haircut',
      resourceName: 'Room 101',
      startDate: '2023-10-01',
      startTime: '10:00 AM',
      endDate: '2023-10-01',
      endTime: '11:00 AM',
    };

    // Use a regular expression to find all placeholders in the format
    const message = format.replace(/\{(\w+)\}/g, (match, placeholder) => {
      // Check if the placeholder exists in the dummyValues object
      return dummyValues[placeholder] !== undefined ? dummyValues[placeholder] : match;
    });

    setTryOutMessage(message);
  };

  const handleSaveOrUpdate = async (data: any) => {
    if (!selectedOrganizationId) return;

    // If SMS is disabled, delete the settings
    if (!data.isSMSEnabled) {
      setIsUpdating(true);
      try {
        await deleteSmsSettings();
        setNotification({
          open: true,
          message: 'SMS settings deleted successfully',
          severity: SEVERITY.SUCCESS,
        });
        setIsEditing(false);
      } catch (err) {
        setNotification({
          open: true,
          message: 'Failed to delete SMS settings',
          severity: SEVERITY.ERROR,
        });
      } finally {
        setIsUpdating(false);
      }
      return;
    }

    // Validate that at least one SMS type is enabled if isSMSEnabled is true
    if (
      data.isSMSEnabled &&
      !data.isScheduleSMSEnabled &&
      !data.isRescheduleSMSEnabled &&
      !data.isCancelSMSEnabled &&
      !data.isReminderSMSEnabled
    ) {
      setNotification({
        open: true,
        message: 'At least one SMS type must be enabled when SMS is enabled.',
        severity: SEVERITY.ERROR,
      });
      return;
    }

    setIsUpdating(true);
    try {
      if (!data.isScheduleSMSEnabled) {
        data.scheduleSMSFormat = '';
      }
      if (!data.isRescheduleSMSEnabled) {
        data.rescheduleSMSFormat = '';
      }
      if (!data.isCancelSMSEnabled) {
        data.cancelSMSFormat = '';
      }
      if (!data.isReminderSMSEnabled) {
        data.reminderSMSFormat = '';
      }

      if (smsSettings) {
        await updateSmsSettings(data);
        setNotification({
          open: true,
          message: 'SMS settings updated successfully',
          severity: SEVERITY.SUCCESS,
        });
      } else {
        // Create new SMS settings
        await saveSmsSettings(data);
        setNotification({
          open: true,
          message: 'SMS settings created successfully',
          severity: SEVERITY.SUCCESS,
        });
      }
      setIsEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save SMS settings';
      setNotification({
        open: true,
        message,
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsUpdating(false);
      refetch();
    }
  };

  useEffect(() => {
    // Initial fetch
    refetch();
  }, [refetch]);

  if (!selectedOrganizationId) {
    return (
      <Alert
        severity="info"
        icon={<Info />}
        sx={{
          borderRadius: theme => theme.shape.borderRadius || '8px',
          boxShadow: theme => theme.shadows[1],
          fontWeight: 500,
        }}
      >
        Please select an organization to view SMS settings.
      </Alert>
    );
  }

  if (isLoading || isUpdating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" icon={<X />}>
        {error}
      </Alert>
    );
  }

  const SmsTypeCard = ({
    title,
    icon,
    enabled,
    format,
    type,
    previewActive = false,
  }: {
    title: string;
    icon: React.ReactNode;
    enabled: boolean;
    format: string;
    type: string;
    previewActive?: boolean;
  }) => {
    return (
      <Card
        elevation={previewActive ? 2 : 1}
        sx={{
          mb: 3,
          borderRadius: 3,
          transition: 'all 200ms cubic-bezier(0.2, 0, 0, 1)',
          border: previewActive ? `2px solid ${theme.palette.primary.main}` : 'none',
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              {icon}
              <Typography variant="h6" ml={1}>
                {title}
              </Typography>
              <Chip
                size="small"
                label={enabled ? 'Enabled' : 'Disabled'}
                color={enabled ? 'success' : 'default'}
                sx={{ ml: 2 }}
              />
            </Box>
            {enabled && format && (
              <Button
                size="small"
                startIcon={previewActive ? <X size={16} /> : <Info size={16} />}
                variant={previewActive ? 'outlined' : 'contained'}
                color={previewActive ? 'error' : 'primary'}
                onClick={() => {
                  if (previewActive) {
                    setTryOutMessage(null);
                    setPreviewType(null);
                  } else {
                    handleTryOut(format, type);
                  }
                }}
                sx={{ borderRadius: '20px' }}
              >
                {previewActive ? 'Close Preview' : 'Preview'}
              </Button>
            )}
          </Box>

          {previewActive && tryOutMessage ? (
            <Card
              elevation={0}
              sx={{
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.primary.main, 0.08),
                border: `1px dashed ${theme.palette.primary.main}`,
                mb: 2,
                borderRadius: '10px',
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.primary.main }}
                  gutterBottom
                  fontWeight="bold"
                >
                  Preview with example data
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    p: 1.5,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: '10px',
                    boxShadow: theme.shadows[1],
                  }}
                >
                  {tryOutMessage}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            enabled &&
            format && (
              <Card
                elevation={0}
                sx={{
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.background.default
                      : theme.palette.grey[50],
                  mb: 2,
                  borderRadius: '10px',
                }}
              >
                <CardContent>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                  >
                    {format}
                  </Typography>
                </CardContent>
              </Card>
            )
          )}

          {!enabled && (
            <Typography variant="body2" color="text.secondary">
              This SMS notification type is currently disabled.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const TemplateField = ({
    name,
    label,
    control,
  }: {
    name: string;
    label: string;
    control: any;
  }) => {
    return (
      <Box my={3} sx={{ position: 'relative' }}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              multiline
              rows={4}
              label={label}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          )}
        />
      </Box>
    );
  };

  return (
    <Box>
      {role !== AUTH_CONSTANTS.ROLES.USER && !isEditing && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            onClick={() => setIsEditing(true)}
            variant="contained"
            disableElevation
            size="small"
            startIcon={<RefreshCw size={16} />}
            sx={{
              borderRadius: theme => theme.shape.borderRadius || '8px',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Configure
          </Button>
        </Box>
      )}

      <Card
        elevation={1}
        sx={{
          mb: 3,
          borderRadius: 3,
          backgroundColor: expandedHelp
            ? theme.palette.background.paper
            : alpha(theme.palette.primary.main, 0.04),
          transition: 'all 200ms cubic-bezier(0.2, 0, 0, 1)',
        }}
      >
        <CardContent>
          <Box
            onClick={() => setExpandedHelp(!expandedHelp)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
          >
            <Box display="flex" alignItems="center">
              <HelpCircle size={20} color="primary" />
              <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 500 }}>
                Template Variables Guide
              </Typography>
            </Box>
            <IconButton size="small">
              {expandedHelp ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </IconButton>
          </Box>

          <Collapse in={expandedHelp}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" paragraph>
              Use these variables in your SMS templates. They will be automatically replaced with
              actual data when messages are sent.
            </Typography>

            <Grid container spacing={2}>
              {templateVariables.map(variable => (
                <Grid item xs={12} sm={6} key={variable.name}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.text.primary, 0.04),
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ mr: 1 }}>{variable.icon}</Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, fontFamily: 'monospace' }}
                          >
                            {variable.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {variable.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {isEditing ? (
        <form onSubmit={handleSubmit(handleSaveOrUpdate)}>
          <Card
            elevation={1}
            sx={{
              mb: 4,
              borderRadius: 3,
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Global Settings
              </Typography>
              <Box mb={2}>
                <Controller
                  name="isSMSEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} color="primary" />}
                      label={
                        <Typography variant="body1" fontWeight={500}>
                          Enable SMS Notifications
                        </Typography>
                      }
                    />
                  )}
                />
              </Box>
            </CardContent>
          </Card>

          {isSMSEnabled && (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card
                    elevation={1}
                    sx={{
                      borderRadius: 3,
                      mb: 3,
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Calendar size={20} />
                        <Typography variant="h6" sx={{ ml: 1 }}>
                          Schedule Notifications
                        </Typography>
                      </Box>
                      <Controller
                        name="isScheduleSMSEnabled"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} color="primary" />}
                            label="Enable Schedule SMS"
                          />
                        )}
                      />

                      {isScheduleSMSEnabled && (
                        <TemplateField
                          name="scheduleSMSFormat"
                          label="Schedule SMS Template"
                          control={control}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card elevation={1} sx={{ borderRadius: 3, mb: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <RefreshCw size={20} />
                        <Typography variant="h6" sx={{ ml: 1 }}>
                          Reschedule Notifications
                        </Typography>
                      </Box>
                      <Controller
                        name="isRescheduleSMSEnabled"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} color="primary" />}
                            label="Enable Reschedule SMS"
                          />
                        )}
                      />

                      {isRescheduleSMSEnabled && (
                        <TemplateField
                          name="rescheduleSMSFormat"
                          label="Reschedule SMS Template"
                          control={control}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card elevation={1} sx={{ borderRadius: 3, mb: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <X size={20} />
                        <Typography variant="h6" sx={{ ml: 1 }}>
                          Cancellation Notifications
                        </Typography>
                      </Box>
                      <Controller
                        name="isCancelSMSEnabled"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} color="primary" />}
                            label="Enable Cancellation SMS"
                          />
                        )}
                      />

                      {isCancelSMSEnabled && (
                        <TemplateField
                          name="cancelSMSFormat"
                          label="Cancellation SMS Template"
                          control={control}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card elevation={1} sx={{ borderRadius: 3, mb: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Bell size={20} />
                        <Typography variant="h6" sx={{ ml: 1 }}>
                          Reminder Notifications
                        </Typography>
                      </Box>
                      <Controller
                        name="isReminderSMSEnabled"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch {...field} checked={field.value} color="primary" />}
                            label="Enable Reminder SMS"
                          />
                        )}
                      />

                      {isReminderSMSEnabled && (
                        <TemplateField
                          name="reminderSMSFormat"
                          label="Reminder SMS Template"
                          control={control}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              onClick={() => setIsEditing(false)}
              variant="outlined"
              sx={{
                borderRadius: theme => theme.shape.borderRadius || '8px',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={isUpdating}
              startIcon={<Check size={18} />}
              sx={{
                borderRadius: theme => theme.shape.borderRadius || '8px',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Save Changes
            </Button>
          </Box>
        </form>
      ) : (
        <>
          <Box mb={3}>
            <Box display="flex" alignItems="center">
              <Mail size={16} style={{ marginRight: '8px' }} />
              <Typography variant="body2" fontWeight={500}>
                SMS Notifications:
              </Typography>
              <Chip
                size="small"
                label={smsSettings?.isSMSEnabled ? 'Active' : 'Inactive'}
                color={smsSettings?.isSMSEnabled ? 'success' : 'default'}
                sx={{ ml: 2, fontWeight: 500 }}
              />
            </Box>
          </Box>

          {!smsSettings?.isSMSEnabled ? (
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.text.primary, 0.02),
                border: `1px dashed ${theme.palette.divider}`,
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={2}
                >
                  <MessageCircle size={48} />
                  <Typography variant="h6" color="text.secondary" mt={2}>
                    SMS Notifications are Disabled
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
                    Enable SMS notifications to automatically keep your customers informed about
                    their appointments.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <>
              <SmsTypeCard
                title="Schedule Notifications"
                icon={<Calendar size={20} />}
                enabled={smsSettings?.isScheduleSMSEnabled || false}
                format={smsSettings?.scheduleSMSFormat || ''}
                type="schedule"
                previewActive={previewType === 'schedule'}
              />

              <SmsTypeCard
                title="Reschedule Notifications"
                icon={<RefreshCw size={20} />}
                enabled={smsSettings?.isRescheduleSMSEnabled || false}
                format={smsSettings?.rescheduleSMSFormat || ''}
                type="reschedule"
                previewActive={previewType === 'reschedule'}
              />

              <SmsTypeCard
                title="Cancellation Notifications"
                icon={<X size={20} />}
                enabled={smsSettings?.isCancelSMSEnabled || false}
                format={smsSettings?.cancelSMSFormat || ''}
                type="cancel"
                previewActive={previewType === 'cancel'}
              />

              <SmsTypeCard
                title="Reminder Notifications"
                icon={<Bell size={20} />}
                enabled={smsSettings?.isReminderSMSEnabled || false}
                format={smsSettings?.reminderSMSFormat || ''}
                type="reminder"
                previewActive={previewType === 'reminder'}
              />
            </>
          )}
        </>
      )}

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default SmsSettings;
